import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, map, of, switchMap, tap } from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { ProjectStorage } from '../../core/services/project.storage';
import { AppSelector } from '../../core/store';
import { ProjectRequester } from '../../requesters';
import { ProjectAdapter } from '../project.adapter';
import {
	createNewProject,
	deleteProject,
	deleteProjectFail,
	deleteProjectSuccess,
	fetchIsProjectDeletableSuccess,
	fetchProjectElementsSuccess,
	fetchProjectsFail,
	fetchProjectsSuccess,
	fetchProjectsWithViewSettingsSuccess,
	getProjectElements,
	getProjectMembers,
	getProjectMembersSuccess,
	initProjects,
	isProjectDeletable,
	modifySearchTerm,
	navigateToOrphanProjects,
	navigateToProject,
	navigateToProjectEdit,
	orphanProjectsCheck,
	orphanProjectsCheckFail,
	orphanProjectsCheckSuccess,
	refreshProjects,
	sendEmailToAdmin,
	sendVerificationCode,
	setDefaultProject,
	setFavoriteProject,
	switchProjectsViewMode,
} from './project.actions';
import { ProjectSelector } from './project.selector';

@Injectable()
export class ProjectEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToProject = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToProject),
				tap((a) => {
					const module = this.projectStorage.getModule();
					if (module === 'handle') {
						this.stateService.go(states.HandleHome, {
							projectId: `${a.id}`,
						});
					} else {
						this.stateService.go(states.GenericsHome, {
							projectId: `${a.id}`,
						});
					}
				}),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public navigateToProjectEdit = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToProjectEdit),
				tap((a) =>
					this.stateService.go(states.ProjectEdit, {
						projectId: `${a.id}`,
					})
				),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public navigateToOrphanProjects = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToOrphanProjects),
				tap(() => this.stateService.go(states.OrphanProjects)),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public createNewProject = createEffect(
		() =>
			this.action$.pipe(
				ofType(createNewProject),
				tap(() => this.stateService.go(states.ProjectCreate)),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################

	public initProjects$ = createEffect(() =>
		this.action$.pipe(
			ofType(initProjects),
			switchMap(() => this.projectRequester.getProjects()),
			map((projects) =>
				fetchProjectsWithViewSettingsSuccess({
					projectList: this.projectAdapter.generateProjectList(projects),
					term: this.projectStorage.getProjectsSearchTerm(),
					viewMode: this.projectStorage.getProjectsViewMode(),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				fetchProjectsFail(error);
				return caught;
			})
		)
	);

	public orphanProjectsCheck$ = createEffect(() =>
		this.action$.pipe(
			ofType(orphanProjectsCheck),
			switchMap(() => this.projectRequester.getOrphanProjects()),
			map((orphanProjects) =>
				orphanProjectsCheckSuccess({
					hasOrphanProjects: orphanProjects.length > 0,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				orphanProjectsCheckFail(error);
				return caught;
			})
		)
	);

	public refreshProjects$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshProjects),
			switchMap(() => this.projectRequester.getProjects(false)),
			map((response) =>
				fetchProjectsSuccess({
					projectList: this.projectAdapter.generateProjectList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				fetchProjectsFail(error);
				return caught;
			})
		)
	);

	public setDefaultProject$ = createEffect(() =>
		this.action$.pipe(
			ofType(setDefaultProject),
			switchMap((a) =>
				this.projectRequester.setDefaultProject(a.id, a.isDefault)
			),
			map(() => refreshProjects()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public setFavoriteProject$ = createEffect(() =>
		this.action$.pipe(
			ofType(setFavoriteProject),
			switchMap((a) =>
				this.projectRequester.setFavoriteProject(a.id, a.isFavorite)
			),
			map(() => refreshProjects()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public getProjectMembers$ = createEffect(() =>
		this.action$.pipe(
			ofType(getProjectMembers),
			switchMap((a) =>
				combineLatest([
					this.projectRequester.getProjectMembers(a.projectId),
					of(a.projectId),
				])
			),
			map(([response, projectId]) =>
				getProjectMembersSuccess({
					projectId,
					members: this.projectAdapter.generateMemberList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public sendEmailToAdmin$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(sendEmailToAdmin),
				switchMap((a) =>
					this.projectRequester.sendEmail(a.projectId, a.adminId, {
						subject: a.title,
						body: a.message,
					})
				),
				map(
					() => this.ngCommService.notifyOnSuccess('message sent') // FIXME: translation
				),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public getProjectDeletable$ = createEffect(() =>
		this.action$.pipe(
			ofType(isProjectDeletable),
			switchMap((a) =>
				combineLatest([this.projectRequester.isProjectUsed(a.id), of(a.id)])
			),
			map(([isUsed, id]) =>
				fetchIsProjectDeletableSuccess({
					id,
					isDeletable: !isUsed,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public getProjectDcElements$ = createEffect(() =>
		this.action$.pipe(
			ofType(getProjectElements),
			switchMap((a) =>
				combineLatest([
					this.projectRequester.getProjectElements(a.id),
					this.projectRequester.isProjectCritical(a.id),
					of(a.id),
				])
			),
			map(([elements, isCritical, id]) =>
				fetchProjectElementsSuccess({
					id,
					elements: this.projectAdapter.generateProjectElementsEntity(elements),
					isCritical,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public sendVerificationCode$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(sendVerificationCode),
				switchMap((a) => this.projectRequester.sendVerificationCode(a.id)),
				catchError((error: HttpErrorResponse, caught) => {
					console.error(error);
					return caught;
				})
			),
		{
			dispatch: false,
		}
	);

	public deleteProject$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteProject),
			switchMap((a) =>
				combineLatest([
					of(a.id),
					this.projectRequester.deleteProject(a.id, a.verificationCode),
				])
			),
			map(([id]) =>
				deleteProjectSuccess({
					id,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				this.store.dispatch(deleteProjectFail());
				console.error(error);
				return caught;
			})
		)
	);

	public refreshAfterProjectDelete$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteProjectSuccess),
			map(() => {
				this.modalService.closeActiveModal({
					isDeleted: true,
				});
				return refreshProjects();
			})
		)
	);

	public switchViewMode$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(switchProjectsViewMode),
				tap((a) => this.projectStorage.setProjectsViewMode(a.viewMode))
			),
		{
			dispatch: false,
		}
	);

	public modifySearchTerm$ = createEffect(
		() =>
			this.action$.pipe(
				ofType(modifySearchTerm),
				tap((a) => this.projectStorage.setProjectsSearchTerm(a.term))
			),
		{
			dispatch: false,
		}
	);

	public constructor(
		private readonly action$: Actions,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly projectAdapter: ProjectAdapter,
		private readonly projectSelector: ProjectSelector,
		private readonly appSelector: AppSelector,
		private readonly projectRequester: ProjectRequester,
		private readonly projectStorage: ProjectStorage,
		private readonly ngCommService: NgCommService
	) {}
}
